/** @format */

import { AnalyticsService, DynamicTemplateLoader, WINDOW } from '@shared/data-access/services';
import {
	BrandStyliticBundleConfig,
	BrandStyliticConfig,
	StyliticsBundleConfig,
	StyliticsElementItem,
	StyliticsItem,
	StyliticsWidget
} from '@pdp/data-access/interfaces';
import { Chain, Environment, ProcessedProduct } from '@shared/data-access/interfaces';
import {
	ChangeDetectionStrategy,
	Component,
	ComponentRef,
	ElementRef,
	NgZone,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	effect,
	inject,
	input,
	signal
} from '@angular/core';

import { HmfBadgeComponent } from '@dsg-tech/homefield-angular';
import { Store } from '@ngrx/store';
import { isPlatformBrowser } from '@angular/common';
import { styliticsConfig } from './stylitics-constants';
import { styliticsShopTheLookWidgetMounted } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-component-stylitics',
	templateUrl: './stylitics.component.html',
	styleUrl: './stylitics.component.scss',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class StyliticsComponent implements OnInit, OnDestroy {
	private readonly analyticsService = inject(AnalyticsService);
	readonly chain = inject(Chain);
	readonly environment = inject(Environment);
	private readonly elementRef = inject(ElementRef);
	protected readonly window = inject<Window>(WINDOW);
	private readonly dynamicTemplateLoader = inject(DynamicTemplateLoader);
	private readonly ngZone = inject(NgZone);
	private readonly store = inject(Store);
	private readonly platformId = inject(PLATFORM_ID);

	/** @description Stylitics Kill switch */
	readonly styliticsEnabled = input.required<boolean>();

	/** @description Enable/Disable Badge for "Model Is Wearing" */
	readonly shopTheLookEnabled = input.required<boolean>();
	/** @description The current Product */
	readonly product = input.required<ProcessedProduct>();

	/** @description The selected color for the product */
	readonly selectedColorAttribute = input<string>();
	previousColorAttribute;

	/** @description Enable/Disable Stylitics MnM */
	readonly styliticsMnMEnabled = input.required<boolean>();

	/** @description  Show/Hide Complete The Set Header */
	readonly showHeader = signal<boolean>(false);

	/** @description  The stylitics widget instance */
	private styliticsWidget: StyliticsWidget;

	private lazyHmfBadge: ComponentRef<HmfBadgeComponent> | undefined;

	/** @description The chain identifier abbreviation */
	get chainIdentifier(): string {
		return this.chain.chainIdentifierAbbr;
	}

	/** @description The chain configuration object */
	get chainConfig(): BrandStyliticConfig {
		return styliticsConfig[this.chainIdentifier];
	}

	constructor() {
		/**
		 * @description Refreshes stylitics widget whenever a different color swatch is selected.
		 */
		effect(() => {
			const currentSelectedColor = this.selectedColorAttribute();
			const productId = this.product().partNumber;
			const itemNumber = currentSelectedColor
				? `${productId}_${currentSelectedColor}`
				: `${productId}`;
			if (this.previousColorAttribute !== currentSelectedColor && this.styliticsWidget) {
				this.previousColorAttribute = currentSelectedColor;
				this.styliticsWidget.refresh({
					modules: [
						{
							type: 'classic',
							params: {
								api: {
									item_number: itemNumber.toUpperCase()
								}
							}
						}
					]
				});
			}
		});
	}

	ngOnInit() {
		// Killswitch is being pulled from the in-stock-default component
		if (isPlatformBrowser(this.platformId) && this.chainConfig && this.styliticsEnabled()) {
			this.ngZone.runOutsideAngular(() => {
				this.initializeStylitics();
			});
		}
	}

	/**
	 * @description Initializes the stylitics widget and calls
	 * private helper method to attach event handlers.
	 */
	private initializeStylitics() {
		const styliticsScript = document.createElement('script');
		styliticsScript.type = 'text/javascript';

		styliticsScript.src = 'https://web-assets.stylitics.com/loader/latest/composer.js';

		// check if script is already initialized
		if (this.window.StyliticsComposer) {
			this.onLoadStylitics();
		} else {
			this.elementRef.nativeElement.appendChild(styliticsScript).onload = async () =>
				this.onLoadStylitics();
		}
	}

	/**
	 * @description Handles the loading of the stylitics widget.
	 */
	async onLoadStylitics() {
		const productId = this.product().partNumber;
		const itemNumber = this.selectedColorAttribute()
			? `${productId}_${this.selectedColorAttribute()}`
			: `${productId}`;
		const viewDetailsCTA = this.shopTheLookEnabled() ? 'Shop This Set' : 'Shop The Set';
		this.styliticsWidget = await this.window.StyliticsComposer.initialize({
			client: this.chainConfig.name,
			target: 'pdp-stylitics-target',
			config: {
				modules: [
					{
						type: 'classic',
						params: {
							api: {
								min: 3,
								max: 3,
								item_number: itemNumber.toUpperCase()
							},
							text: {
								viewDetailsCTA,
								itemLinkCTA: 'SHOP'
							},
							display: {
								disableMnM: !this.styliticsMnMEnabled()
							}
						},
						on: {
							'mount.bundles': (data: StyliticsBundleConfig) => {
								this.setupStyliticsCSS(data);
								this.store.dispatch(
									styliticsShopTheLookWidgetMounted({
										styliticsWidgetShown:
											this.shopTheLookEnabled() && !data.hidden
									})
								);
							},
							'mount.outfit': (data: StyliticsElementItem) => {
								if (this.shopTheLookEnabled()) {
									this.setupModelOutfitBadge(data);
								}
							},
							'click.bundle': (item: StyliticsElementItem) => {
								// fires when the bundle is clicked
								this.analyticsService.triggerAnalyticsEvent(
									'styliticsSTLClicked',
									item
								);
							}
						},
						override: {
							'click.item': (item: { item: StyliticsItem }) => {
								const remoteId = item?.item?.remote_id
									? item.item.remote_id.split('_')[0]
									: null;
								const itemColor = item?.item?.other_client_item_ids?.color;
								if (remoteId && itemColor) {
									window.quickview.openProductQuickView(remoteId, 'stylitics', {
										defaultColor: itemColor
									});
									this.analyticsService.triggerAnalyticsEvent(
										'styliticsShopClicked',
										item
									);
								} else if (remoteId) {
									window.quickview.openProductQuickView(remoteId, 'stylitics');
									this.analyticsService.triggerAnalyticsEvent(
										'styliticsShopClicked',
										item
									);
								}
							}
						}
					}
				]
			}
		});

		// render the widget to its target div
		this.styliticsWidget.start();
	}

	/**
	 * @description Querys the DOM to retrieve stylitics header and
	 * component to apply css styles to it based on the chain and
	 * defined styles within the constants file.
	 */
	private setupStyliticsCSS(data: StyliticsBundleConfig) {
		const styliticsElement = document.getElementById('pdp-stylitics-component');
		if (styliticsElement) {
			if (data.bundles?.length) {
				this.showHeader.set(true);
				this.applyStylesAndClasses(styliticsElement, this.chainConfig.bundles);
			} else {
				this.showHeader.set(false);
				this.applyStylesAndClasses(styliticsElement, this.chainConfig.nonBundles);
			}
		}
	}

	private applyStylesAndClasses(element: HTMLElement, config: BrandStyliticBundleConfig) {
		Object.assign(element.style, config.componentStyle);
		if (config.componentClassesToAdd) {
			element.classList.add(config.componentClassesToAdd);
		}
		if (config.componentClassesToRemove) {
			element.classList.remove(config.componentClassesToRemove);
		}
	}

	/**
	 * @description Adds an hmf-badge to the first bundle in the stylitics widget.
	 */
	setupModelOutfitBadge(data: StyliticsElementItem) {
		const outfit = data.outfit;
		if (outfit['on-model-image']) {
			const styliticsBadgeElement = data.element?.querySelector(
				'.stylitics-bundle-badge:not(.stylitics-bundle-badge-modal)'
			);
			if (styliticsBadgeElement) {
				this.mountLazyHmfBadge(styliticsBadgeElement);
			}
		}
	}

	mountLazyHmfBadge(element: Element) {
		if (!this.lazyHmfBadge) {
			this.lazyHmfBadge = this.dynamicTemplateLoader.createPlainDOMComponent(
				HmfBadgeComponent,
				{ badgeName: 'ModelWearing' }
			);
		}
		element.appendChild(this.lazyHmfBadge.location.nativeElement);
	}

	ngOnDestroy(): void {
		if (this.lazyHmfBadge) {
			this.lazyHmfBadge.destroy();
		}
	}
}
